import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Trash2, ShieldAlert, AlertTriangle, Loader2, Ban, Link2 } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

// Purge is a Global-Administrator-only capability. The server enforces this on
// every endpoint; the client checks role === 'owner' to decide whether to render
// any purge control at all.
export const useOpsPurgeAccess = () => {
  const { user } = useContext(AuthContext);
  return user?.role === 'owner';
};

// A small reusable "Purge selected" button for a record list toolbar.
export const PurgeButton = ({ count, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || count === 0}
    data-testid="ops-purge-btn"
    className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
  >
    <Trash2 size={15} /> Purge{count ? ` (${count})` : ''}
  </button>
);

// Shared review + confirm + execute dialog. Reused by every module.
export const PurgeReviewDialog = ({ entityType, ids, onClose, onDone }) => {
  const { token } = useContext(AuthContext);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [finalConfirm, setFinalConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.post(`${API}/operations/purge/preview`, { entity_type: entityType, entity_ids: ids }, auth(token))
      .then((r) => setPreview(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Preview failed'); onClose(); });
  }, [entityType, JSON.stringify(ids)]);

  const purgeable = (preview?.records || []).filter((r) => r.can_purge);
  const blocked = (preview?.records || []).filter((r) => !r.can_purge);
  const canExecute = purgeable.length > 0 && reason.trim().length >= 3 && finalConfirm
    && email.trim().toLowerCase() === (preview?.owner_email || '').toLowerCase();

  const execute = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/operations/purge/execute`, {
        entity_type: entityType,
        entity_ids: purgeable.map((x) => x.entity_id),
        confirm_text: email.trim(),
        reason: reason.trim(),
        final_confirm: true,
      }, auth(token));
      setResult(r.data);
      toast.success(r.data.message);
      onDone && onDone(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Purge failed'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => !busy && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ops-purge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert size={20} /> Permanently Purge — Global Administrator
          </DialogTitle>
          <DialogDescription>
            This permanently removes the selected {entityType.replace(/_/g, ' ')} record(s) and the items they
            own exclusively (their photos, generated report entries, client-message drafts and auto-created
            corrective actions / follow-ups). Shared customer, location, personnel and work-order master data
            is never deleted. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {!preview && <div className="flex items-center gap-2 py-8 text-sm text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading impact review…</div>}

        {preview && !result && (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-white" data-testid="ops-purge-review">
              <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                {preview.selected_count} record(s) selected · {purgeable.length} will be purged
                {blocked.length > 0 && ` · ${blocked.length} blocked`}
              </div>
              <div className="divide-y divide-slate-100">
                {preview.records.map((r) => (
                  <div key={r.entity_id} className="px-3 py-2.5 text-sm" data-testid={`ops-purge-record-${r.entity_id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-[#0E2A47]">{r.record_number}</span>
                      {r.record_type && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{r.record_type}</span>}
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{r.record_status || '—'}</span>
                      {r.is_demo && <span className="rounded bg-fuchsia-100 px-1.5 py-0.5 text-[10px] font-bold text-fuchsia-700">DEMO</span>}
                      {r.can_purge
                        ? <span className="ml-auto text-[11px] font-semibold text-red-600">Will be purged</span>
                        : <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700"><Ban size={12} /> Blocked</span>}
                    </div>
                    {(r.location_name || r.record_name) && <div className="mt-0.5 truncate text-xs text-slate-400">{r.record_name || r.location_name}</div>}
                    {(r.owned.length > 0 || r.orphan_storage_blobs > 0) && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {r.owned.map((o) => (
                          <span key={o.collection} className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
                            <Link2 size={10} /> {o.count} {o.label}
                          </span>
                        ))}
                        {r.orphan_storage_blobs > 0 && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{r.orphan_storage_blobs} stored photo(s)</span>}
                      </div>
                    )}
                    {r.blocking.map((b, i) => (
                      <div key={i} className="mt-1 flex items-start gap-1.5 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid={`ops-purge-block-${r.entity_id}`}>
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {b.message}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {purgeable.length > 0 ? (
              <div className="space-y-3 rounded-lg border border-red-200 bg-red-50/40 p-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Reason (required, retained in the audit log)</label>
                  <input data-testid="ops-purge-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Isolated verification records for the purge feature"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-red-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Type your Global Administrator email to authorize: <span className="font-mono normal-case text-slate-600">{preview.owner_email}</span></label>
                  <input data-testid="ops-purge-email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={preview.owner_email}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-red-400" />
                </div>
                <label className="flex items-center gap-2 text-sm font-medium text-red-700">
                  <input type="checkbox" data-testid="ops-purge-final" checked={finalConfirm} onChange={(e) => setFinalConfirm(e.target.checked)} className="h-4 w-4 accent-red-600" />
                  I understand this permanently removes {purgeable.length} record(s) and cannot be undone.
                </label>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                None of the selected records can be purged — resolve the blocking dependencies shown above first.
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-2 text-sm" data-testid="ops-purge-result">
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-700">{result.message}</div>
            {(result.purged || []).map((p) => (
              <div key={p.entity_id} className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600">
                <span className="font-mono font-semibold text-[#0E2A47]">{p.record_number}</span> purged · audit {String(p.audit_id).slice(0, 8)}
                {p.orphaned_storage_blobs > 0 && ` · ${p.orphaned_storage_blobs} stored photo(s) unreferenced`}
              </div>
            ))}
            {(result.skipped || []).length > 0 && <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{result.skipped.length} record(s) skipped (blocked).</div>}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <button onClick={onClose} data-testid="ops-purge-close" className="rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={execute} disabled={!canExecute || busy} data-testid="ops-purge-execute"
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Permanently Purge
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
